// /splitter — EXPERIMENT PAGE. Not linked from anywhere; the live Studio at '/'
// is untouched and unaware of this file.
//
// Tests the LALAL.AI shape end to end, on our own stack:
//   anonymous upload → the first 60 s separated immediately, free, no account
//   → the mixer plays → one task button ("Separate the whole song") → and only
//   THEN an ask.
//
// The two rules that make it honest, and that the live Studio must keep too:
//   1. The automatic preview NEVER spends a credit. Nobody clicked anything, so
//      nothing of theirs may be consumed — that is the nkk failure mode.
//   2. The limit is stated BEFORE the work, not after. LALAL's biggest 1-star
//      cluster is people who processed a file and only then learned they
//      couldn't have it.
//
// Who sees what on the full-song click:
//   • anonymous  → the plan modal (this is the thing being tested)
//   • free       → the plan modal too: on this page the whole song IS the paid
//                  thing, so a free account gets the same 60-second preview
//   • pro        → runs it
// Nothing here consumes a credit: the preview is free and the whole song is
// Pro, so the quota counter is never touched on this page.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';
import { STEM_UI } from '../components/studioIcons';
import { computeEnvelope } from '../components/Waveform';
import { STEMS, StemName, MixState, DEFAULT_MIX, StemEngine, audibleVolume } from '../lib/studioEngine';
import { separateClientSide, decodeFileToStereo, prefetchStandardEngine, engineIsCached } from '../lib/clientSep';
import { cacheSeparation, newSeparationKey } from '../lib/stemCache';
import { claimHandoff } from '../lib/handoffClaim';
import {
  detectChords, chordsPreferFlats, formatChord, keyFromChords, simplifyChords,
  type ChordSegment, type ChordKeyEstimate,
} from '../lib/chordDetect';
import { computePeaks } from '../components/Waveform';
import { sepErrorReason } from '../lib/track';
import {
  isMobileTier, isTwoStemResult, TWO_STEM_OTHER_NAME, MOBILE_STEM_COUNT, DESKTOP_STEM_COUNT,
} from '../lib/deviceTier';
import { ContinueOnDesktop } from '../components/ContinueOnDesktop';
import { funnelEvent, deviceKind, referrerHost, markFunnel } from '../lib/funnel';
import { fmtTime } from '../lib/format';
// The real Studio's stylesheet: this page uses its ACTUAL rack markup
// (.sx-ch/.sx-strip/.sx-lane) so the preview reads as the same product, not a
// second mixer. Importing it is additive — StudioNext is untouched.
import './studio-next.css';
import './splitter.css';

/** The free window, in seconds. Stated up front, never discovered afterwards. */
const PREVIEW_SECONDS = 60;
/**
 * How much of the song free accounts get chords for. Same number the Studio
 * uses (FREE_CHORDS_SECONDS there) and the same number this page's own plan
 * table prints — the three have to agree or the padlock contradicts the table
 * the user is about to read.
 */
const FREE_CHORDS_SECONDS = 30;
/**
 * Progress fraction at which the engine stops setting itself up and starts
 * actually separating (the warm-up owns everything below it). Used only to
 * decide which wording the wait shows.
 */
const SEP_BEGINS_AT = 0.68;
/**
 * The two points on `separateClientSide`'s reported scale that a run can start
 * from. Cold, it opens at 0.05 and spends 0.05–0.60 downloading the weights.
 * Warm, that download never happens and the first real wait is session
 * creation, which the scale places at 0.62. See `progBase`.
 */
const ENGINE_COLD_BASE = 0.05;
const ENGINE_WARM_BASE = 0.62;

/**
 * The lane trace, copied from StudioNext's local `LaneWave` — min/max envelope
 * per column, no silence floor, canvas knockout (the Moises teardown port).
 * A copy rather than an import because that one is module-local to the live
 * page, and promoting it to a shared component would mean editing StudioNext
 * while this is still an experiment. ⚠️ If /splitter is ever promoted, hoist
 * ONE copy into components/Waveform.tsx and delete both.
 */
function LaneWave({ env }: { env: Float32Array }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const draw = () => {
      const r = c.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const dpr = window.devicePixelRatio || 1;
      const W = Math.max(1, Math.round(r.width * dpr * 2));
      const H = Math.max(1, Math.round(r.height * dpr));
      if (c.width !== W || c.height !== H) { c.width = W; c.height = H; }
      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, W, H);
      const m = getComputedStyle(c).color.match(/rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)/);
      const [cr, cg, cb] = m ? [m[1], m[2], m[3]] : ['14', '6', '34'];
      const fill = ctx.createLinearGradient(0, 0, 0, H);
      fill.addColorStop(0, `rgba(${cr}, ${cg}, ${cb}, 0.86)`);
      fill.addColorStop(1, `rgba(${cr}, ${cg}, ${cb}, 0.64)`);
      ctx.fillStyle = fill;
      const n = env.length >> 1;
      const mid = H / 2;
      const half = (H / 2) * 0.92;
      for (let x = 0; x < W; x++) {
        const b0 = Math.floor((x * n) / W);
        const b1 = Math.max(b0 + 1, Math.floor(((x + 1) * n) / W));
        let mn = 0, mx = 0;
        for (let b = b0; b < b1 && b < n; b++) {
          if (env[b * 2] < mn) mn = env[b * 2];
          if (env[b * 2 + 1] > mx) mx = env[b * 2 + 1];
        }
        if (mx - mn <= 0) continue; // silence draws nothing — no floor
        ctx.fillRect(x, mid - mx * half, 1, (mx - mn) * half);
      }
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(c);
    return () => ro.disconnect();
  }, [env]);
  return <canvas ref={ref} className="sx-lane-canvas" aria-hidden />;
}

/* ── the page's own header ──────────────────────────────────────────────────
 * The shared TopBar shows a logged-out visitor nothing but the logo and a Sign
 * in button, because every route it knows about needs an account. This page's
 * audience is the opposite: someone who arrived anonymously from an SEO article
 * and has no account. So the menu points at what they CAN open — the tool pages
 * they came from — and only turns into the app's own nav once they're signed in.
 *
 * Chrome, colours and chip shapes are the Studio's (`--sx-*` tokens, the same
 * 40px controls), so moving from here into the mixer isn't a change of product.
 */
const SITE = 'https://riffloop.app';

/** Mirrors the marketing site's Tools dropdown (see public/site-nav.js). */
const TOOL_GROUPS: { label: string; items: [string, string][] }[] = [
  {
    label: 'Tools',
    items: [
      ['AI Stem Splitter', '/stem-splitter/'],
      ['AI Vocal Remover', '/vocal-remover/'],
      ['Song Key Changer', '/change-song-key/'],
      ['Slow Down Songs', '/slow-down-songs/'],
      ['Backing Track Maker', '/backing-track-maker/'],
      ['AI Chord Finder', '/chord-finder/'],
    ],
  },
  {
    label: 'On YouTube',
    items: [
      ['Stem Splitter for YouTube', '/youtube-stem-splitter/'],
      ['YouTube Vocal Remover', '/youtube-vocal-remover/'],
      ['Isolate Instruments', '/how-to-isolate-instruments-on-youtube/'],
    ],
  },
  {
    label: 'By instrument',
    items: [
      ['Bass Stem Splitter', '/bass-stem-splitter/'],
      ['Drum Stem Splitter', '/drum-stem-splitter/'],
      ['Guitar Stem Splitter', '/guitar-stem-splitter/'],
      ['Piano Stem Splitter', '/piano-stem-splitter/'],
    ],
  },
];

function SplitterNav() {
  const { user } = useAuth();
  const [toolsOpen, setToolsOpen] = useState(false);
  const [mobOpen, setMobOpen] = useState(false);
  const toolsRef = useRef<HTMLDivElement>(null);
  const [theme, setTheme] = useState<'dark' | 'light'>(() =>
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark',
  );

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    document.documentElement.setAttribute('data-theme', next);
    try { localStorage.setItem('rl-theme', next); } catch { /* ignore */ }
  };

  // `pointerdown` rather than `mousedown`: on a touch screen the synthesized
  // mouse event only lands after the finger lifts, which left the panel open
  // under the tap that was meant to dismiss it.
  useEffect(() => {
    if (!toolsOpen) return;
    const onDown = (e: PointerEvent) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target as Node)) setToolsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setToolsOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey); };
  }, [toolsOpen]);

  const caret = (
    <svg className="sp-nav-caret" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9.5l6 6 6-6" />
    </svg>
  );

  // Signed in, the same four destinations the Studio's rail offers, in the same
  // order and wording — so this reads as the app's nav laid on its side, not a
  // second navigation with its own vocabulary.
  const appLinks = (
    <>
      <Link className="sp-nav-i" to="/">Studio</Link>
      <Link className="sp-nav-i" to="/songs">Your songs</Link>
      <Link className="sp-nav-i" to="/sections">Saved loops</Link>
      <Link className="sp-nav-i" to="/pricing">Plans</Link>
    </>
  );

  const siteLinks = (
    <>
      <a className="sp-nav-i" href={`${SITE}/#practice`}>Practice</a>
      <a className="sp-nav-i" href={`${SITE}/learn/`}>Learn</a>
      <a className="sp-nav-i" href={`${SITE}/#pricing`}>Pricing</a>
    </>
  );

  return (
    <header className="sp-top">
      <div className="sp-top-in">
        <Logo size="sm" linkTo={user ? '/dashboard' : `${SITE}/`} />

        <nav className="sp-nav">
          {user ? appLinks : (
            <>
              <div className="sp-nav-wrap" ref={toolsRef}>
                <button
                  className={`sp-nav-i sp-nav-btn${toolsOpen ? ' on' : ''}`}
                  onClick={() => setToolsOpen((v) => !v)}
                  aria-expanded={toolsOpen}
                  aria-haspopup="true"
                >
                  Tools {caret}
                </button>
                {toolsOpen && (
                  <div className="sp-menu" role="menu">
                    {TOOL_GROUPS.map((g) => (
                      <div className="sp-menu-g" key={g.label}>
                        <div className="sp-menu-h">{g.label}</div>
                        {g.items.map(([label, href]) => (
                          <a className="sp-menu-i" key={href} href={SITE + href} role="menuitem">{label}</a>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {siteLinks}
            </>
          )}
        </nav>

        <span className="sp-top-sp" />

        <button
          className="sp-top-theme"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="4.5" />
              <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
            </svg>
          ) : (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
            </svg>
          )}
        </button>

        <Link className="sp-top-cta" to={user ? '/' : '/login'}>
          {user ? 'Open Studio' : 'Sign in'}
        </Link>

        {/* Phone: the link row collapses into this. Kept out of the account
            menu pattern deliberately — there is no account here to hang it on. */}
        <button
          className="sp-top-burger"
          onClick={() => setMobOpen((v) => !v)}
          aria-expanded={mobOpen}
          aria-label="Menu"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            {mobOpen ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
          </svg>
        </button>
      </div>

      {mobOpen && (
        <div className="sp-mobmenu">
          {user ? appLinks : (
            <>
              {TOOL_GROUPS.map((g) => (
                <div className="sp-menu-g" key={g.label}>
                  <div className="sp-menu-h">{g.label}</div>
                  {g.items.map(([label, href]) => (
                    <a className="sp-menu-i" key={href} href={SITE + href}>{label}</a>
                  ))}
                </div>
              ))}
              <div className="sp-menu-g">{siteLinks}</div>
            </>
          )}
        </div>
      )}
    </header>
  );
}

/**
 * The separation loader: one signal in, several out.
 *
 * Same idea and the same motion as LALAL's "generating previews" mark (owner's
 * reference) — a pulse runs in along the left rail, reaches the splitter node,
 * and three pulses leave together along the branches — drawn in our own palette
 * and with our own centre glyph rather than lifting theirs. It says what the
 * wait IS, which a spinner never can.
 *
 * `pathLength="100"` on every path is what makes one dash definition travel
 * correctly along rails of different real lengths.
 */
const RAIL_MID = 'M78 66 H122';
const RAIL_UP = 'M78 66 H92 Q100 66 100 58 V44 Q100 36 108 36 H122';
const RAIL_DOWN = 'M78 66 H92 Q100 66 100 74 V88 Q100 96 108 96 H122';
const RAIL_IN = 'M10 66 H54';

function SplitLoader() {
  return (
    <svg className="sp-split" viewBox="0 0 132 132" aria-hidden="true">
      <circle className="sp-split-ring" cx="66" cy="66" r="54" />
      {[RAIL_IN, RAIL_MID, RAIL_UP, RAIL_DOWN].map((d) => (
        <path className="sp-split-rail" key={d} d={d} pathLength="100" />
      ))}
      <path className="sp-split-pulse sp-in" d={RAIL_IN} pathLength="100" />
      {[RAIL_MID, RAIL_UP, RAIL_DOWN].map((d) => (
        <path className="sp-split-pulse sp-out" key={d} d={d} pathLength="100" />
      ))}
      <rect className="sp-split-node" x="53" y="53" width="26" height="26" rx="8" />
      {/* Three bars — the stems — not their division sign. */}
      <path className="sp-split-glyph" d="M60 60.5 H72 M60 66 H72 M60 71.5 H72" />
    </svg>
  );
}

type Phase = 'idle' | 'decoding' | 'separating' | 'ready' | 'error';

interface Decoded {
  left: Float32Array;
  right: Float32Array;
  sampleRate: number;
  durationSec: number;
  name: string;
}

export function Splitter() {
  const { user } = useAuth();
  const isPro = user?.plan === 'pro';
  const tier = !user ? 'anonymous' : isPro ? 'pro' : 'free';
  const tierRef = useRef(tier);
  tierRef.current = tier;
  /**
   * One funnel event → GA4 AND our own database (see lib/funnel.ts), always
   * stamped with the visitor's tier. Reads the tier through a ref so the
   * memoised callbacks below never report a stale plan.
   */
  function fx(event: string, p: Record<string, string | number | boolean | undefined> = {}) {
    funnelEvent(event, { tier: tierRef.current, ...p });
  }

  const [phase, setPhase] = useState<Phase>('idle');
  const [prog, setProg] = useState<{ frac: number; label?: string }>({ frac: 0 });
  /**
   * Where THIS run's progress really starts on the engine's reported scale.
   *
   * That scale reserves 0.05–0.60 for downloading the weights and only
   * 0.70–0.99 for the separation itself. The download is skipped entirely once
   * the weights are cached — i.e. on every visit after the first — so the bar
   * leapt to 70% within a second and then crawled through all of the actual
   * work in the remaining 30%. Rescaling to the band this run will actually
   * traverse is what makes the number mean something.
   */
  const [progBase, setProgBase] = useState(ENGINE_COLD_BASE);
  const [decoded, setDecoded] = useState<Decoded | null>(null);
  const [mix, setMix] = useState<MixState>(DEFAULT_MIX);
  const [emptyStems, setEmptyStems] = useState<Set<StemName>>(new Set());
  /**
   * Phones and tablets run a 2-stem engine (vocals / instrumental) — see
   * lib/deviceTier and /vendor/mobile-tier.js. This page used to promise six
   * parts, print six coloured chips and raise a six-lane skeleton on every
   * device, then hand a phone two lanes with the instrumental filed under
   * "Solo & Lead". `phone` is what stops it saying things this device can't do.
   */
  const phone = useMemo(() => isMobileTier(), []);
  const stemCount = phone ? MOBILE_STEM_COUNT : DESKTOP_STEM_COUNT;
  /** The rack channels this device can actually fill, in rack order. */
  const shownStems = useMemo(
    () => (phone ? STEM_UI.filter((s) => s.id === 'vocals' || s.id === 'other') : STEM_UI),
    [phone],
  );
  /**
   * Keyed off the RESULT, not the device: whatever we print about a finished
   * run has to be backed by the audio in front of it. (They agree in practice —
   * a 2-stem result only happens on the 2-stem engine — but a desktop run that
   * somehow returned two stems would still be described correctly.)
   */
  const twoStem = phase === 'ready' && isTwoStemResult(emptyStems as Set<string>);
  /** "Solo & Lead" is the melodic lead; a 2-stem run's `other` is everything. */
  const stemName = (id: StemName, name: string) =>
    (twoStem || phone) && id === 'other' ? TWO_STEM_OTHER_NAME : name;
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [plansOpen, setPlansOpen] = useState(false);
  // The extension's modal, with ONE deliberate divergence: it opens on YEARLY,
  // not monthly. The extension faces someone who already installed it and is
  // mid-practice, where a low entry price is the friction that matters. This
  // page faces an anonymous first-session stranger, and there both LALAL and
  // Moises default to annual — the "save 45%" anchor only reads as a discount
  // if the discounted option is the one on screen. It also matches what our
  // payers actually bought (annual + lifetime; the one monthly subscriber
  // churned). Monthly stays one click away.
  // ⚠️ Revert = one word here. It does NOT affect the extension's own default
  // (owner's call, 2026-08-21), which is a separate surface.
  const [upPlan, setUpPlan] = useState<'pro' | 'lifetime'>('pro');
  const [upBilling, setUpBilling] = useState<'monthly' | 'yearly'>('yearly');
  /** Per-stem min/max envelopes — what makes a lane read as tape, not a bar. */
  const [lanePeaks, setLanePeaks] = useState<Partial<Record<StemName, Float32Array>>>({});
  /** The chord timeline over the separated window, and the key it implies. */
  const [chords, setChords] = useState<ChordSegment[] | null>(null);
  const [chordsBusy, setChordsBusy] = useState(false);
  const [chordKey, setChordKey] = useState<ChordKeyEstimate | null>(null);
  const [chordLaneW, setChordLaneW] = useState(0);
  /** Guards a late result from a track the user has already moved on from. */
  const chordRunRef = useRef(0);
  /** True once the FULL song has been separated (so the CTA retires). */
  const [full, setFull] = useState(false);
  /** They said "keep using free" — the task row becomes the free doorway. */
  const [declinedFree, setDeclinedFree] = useState(false);
  const [handingOff, setHandingOff] = useState(false);
  /** The original file, kept so the Studio handoff can carry the source too. */
  const sourceFileRef = useRef<File | null>(null);
  /** Current stem blob URLs, for the handoff to re-fetch into Blobs. */
  const stemUrlsRef = useRef<Record<StemName, string> | null>(null);
  /** Latest `onFile`, so the mount-time handoff claim can reach it. */
  const onFileRef = useRef<((f: File) => Promise<void>) | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [masterVol, setMasterVol] = useState(1);
  const [preMuteVol, setPreMuteVol] = useState(1);
  /** Seconds since real separation began — drives the wait's wording. */
  const [sepElapsed, setSepElapsed] = useState(0);
  const sepStartedRef = useRef<number | null>(null);
  /** Aborts the run in flight; see cancelRun. */
  const abortRef = useRef<AbortController | null>(null);

  // ── Funnel bookkeeping (telemetry only — nothing here changes behaviour) ──
  /**
   * 'handoff' when an SEO page sent the file over, 'direct' for a drop here,
   * 'pick' when a WebKit visitor was sent here to choose the file (Safari
   * partitions the same-site storage bridge away — see handoffClaim.ts), and
   * 'repick' once a hand-off failed and the visitor chose the file again, so a
   * recovery is never counted as a hand-off that worked.
   */
  const arrivalRef = useRef<'handoff' | 'direct' | 'pick' | 'repick'>('direct');
  /** Raw engine progress of the run in flight — what a cancel/leave reports. */
  const lastFracRef = useRef(0);
  /** Mirrors `phase` for the pagehide handler, which outlives any render. */
  const phaseRef = useRef<Phase>('idle');
  phaseRef.current = phase;
  /** Has the plan modal been seen at all this page-life? */
  const modalSeenRef = useRef(false);
  /** When the modal opened, for dwell time on close. */
  const plansOpenedAt = useRef(0);
  /** First-time-per-track flags: "did they listen / touch the mixer" is one
      row each, not a stream of every fader move. Reset per track. */
  const firstsRef = useRef<Set<string>>(new Set());
  const mountedAtRef = useRef(Date.now());
  function once(key: string, event: string, p: Record<string, string | number | boolean | undefined> = {}) {
    if (firstsRef.current.has(key)) return;
    firstsRef.current.add(key);
    fx(event, p);
  }
  /** Open the plan modal AND record that the upgrade message was seen. */
  function openPlans(from: string) {
    plansOpenedAt.current = Date.now();
    modalSeenRef.current = true;
    setPlansOpen(true);
    fx('rl_splitter_modal_open', { from });
  }
  /** Close it without choosing — how, and after how long, with what selected. */
  function closePlans(via: string) {
    setPlansOpen(false);
    fx('rl_splitter_modal_close', {
      via, plan: upPlan, billing: upBilling,
      secs: Math.round((Date.now() - plansOpenedAt.current) / 1000),
    });
  }

  const engineRef = useRef<StemEngine | null>(null);
  if (!engineRef.current) engineRef.current = new StemEngine();
  const engine = engineRef.current;

  /**
   * Funnel handoff (?handoff=<id>): an SEO tool page on riffloop.app stashed the
   * visitor's file in the same-site bridge and sent them here. Claim it and run
   * the preview immediately — no account, nothing spent. This is what makes the
   * article → splitter trip feel like one motion instead of a page change.
   *
   * Guarded by a ref: StrictMode double-invokes effects in dev, and the bridge
   * DELETES the record on first read, so a second claim resolves null and would
   * overwrite the success with the error. Same shape as StudioNext's claim.
   */
  const handoffClaimRef = useRef(false);
  useEffect(() => {
    let handoffId: string | null = null;
    let pick = false;
    try {
      const params = new URL(window.location.href).searchParams;
      handoffId = params.get('handoff');
      pick = params.get('pick') === '1';
    } catch { /* ignore */ }
    if (pick && !handoffId) {
      // A WebKit visitor, sent over from an article's dropzone to choose the
      // file HERE: in Safari the bridge iframe cannot see riffloop.app's
      // storage, so the article never stashes a file for us to claim. Nothing
      // to do but remember which door they came through.
      arrivalRef.current = 'pick';
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('pick');
        window.history.replaceState(null, '', url.toString());
      } catch { /* ignore */ }
      return;
    }
    if (!handoffId || handoffClaimRef.current) return;
    handoffClaimRef.current = true;
    arrivalRef.current = 'handoff';
    // Strip it before the async claim — the record is one-shot, so a reload
    // must not try (and fail) to claim it again.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('handoff');
      window.history.replaceState(null, '', url.toString());
    } catch { /* ignore */ }
    setPhase('decoding');
    void claimHandoff(handoffId).then((file) => {
      if (file) { void onFileRef.current?.(file); return; }
      // The message below asks them to choose the file again. That re-pick is
      // a recovery, not a hand-off that worked — label it so.
      arrivalRef.current = 'repick';
      fx('rl_splitter_error', { stage: 'handoff' });
      setErr("That song didn't carry over — choose the file again to load it.");
      setPhase('error');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fx('rl_splitter_view', { source: arrivalRef.current, device: deviceKind(), ref: referrerHost() });
    // Warm the engine while they're still choosing a file — the download is the
    // long pole on a cold visit, not the inference.
    prefetchStandardEngine();
    return () => engine.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Where did they leave, and when? `phase` at exit is the abandonment point:
  // 'separating' + pct says they gave up during the wait, 'ready' says they
  // heard it. pagehide covers a closed tab / a full navigation (the Studio
  // hand-off is one); the effect cleanup covers in-app route changes. Once per
  // page-life either way. fetch keepalive lets the beacon outlive the page.
  useEffect(() => {
    let left = false;
    const leave = (via: string) => {
      if (left) return;
      left = true;
      fx('rl_splitter_leave', {
        via,
        phase: phaseRef.current,
        pct: Math.round(lastFracRef.current * 100),
        secs: Math.round((Date.now() - mountedAtRef.current) / 1000),
        played: firstsRef.current.has('play'),
        modal: modalSeenRef.current,
      });
    };
    const onHide = () => leave('unload');
    window.addEventListener('pagehide', onHide);
    return () => { window.removeEventListener('pagehide', onHide); leave('nav'); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    engine.setCallbacks({
      onTimeUpdate: (t, d) => { setPos(t); if (d) setDur(d); },
      onEnded: () => setPlaying(false),
    });
  }, [engine]);

  // Start the clock the first time progress crosses out of set-up, and tick it
  // once a second so the wording can move on. Reset whenever a run ends.
  useEffect(() => {
    if (phase !== 'separating') { sepStartedRef.current = null; setSepElapsed(0); return; }
    if (prog.frac >= SEP_BEGINS_AT && sepStartedRef.current == null) {
      sepStartedRef.current = Date.now();
      setSepElapsed(0);
    }
    if (sepStartedRef.current == null) return;
    const id = setInterval(() => {
      const started = sepStartedRef.current;
      if (started != null) setSepElapsed(Math.round((Date.now() - started) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [phase, prog.frac >= SEP_BEGINS_AT]);

  const runSeparation = useCallback(async (d: Decoded, seconds: number | null) => {
    setPhase('separating');
    setProg({ frac: 0 });
    const started = Date.now();
    const n = seconds == null ? d.left.length : Math.min(d.left.length, Math.round(seconds * d.sampleRate));
    const ac = new AbortController();
    abortRef.current = ac;
    // Cheap Cache Storage lookup, and it only matches a COMPLETE download —
    // partial ranges live under their own keys — so a resumed download is
    // correctly treated as cold.
    const warm = await engineIsCached().catch(() => false);
    setProgBase(warm ? ENGINE_WARM_BASE : ENGINE_COLD_BASE);
    const stage = seconds == null ? 'full' : 'preview';
    lastFracRef.current = 0;
    fx(`rl_splitter_${stage}_start`, { warm, dur: Math.round(d.durationSec) });
    try {
      const res = await separateClientSide(
        d.left.subarray(0, n), d.right.subarray(0, n), d.sampleRate,
        (frac, label) => { lastFracRef.current = frac; setProg({ frac, label }); },
        { signal: ac.signal },
      );
      await engine.load(res.stemUrls);
      stemUrlsRef.current = res.stemUrls;
      // Everything up. DEFAULT_MIX is vocals-only (the Studio's isolate default),
      // which would open this page on a lone vocal — the demo has to be the WHOLE
      // band first, so muting a part is the user's own discovery.
      const next: MixState = {
        ...DEFAULT_MIX,
        stems: Object.fromEntries(
          STEMS.map((s) => [s, { volume: 1, solo: false, mute: false }]),
        ) as MixState['stems'],
        loop: false,
      };
      setMix(next);
      engine.applyMix(next);
      setEmptyStems(res.emptyStems);
      const secs = n / d.sampleRate;
      setDur(secs);
      // Bound playback to what was actually separated, and park the playhead
      // at the top — otherwise the window is 0..0 from construction.
      engine.setWindow(0, secs);
      setPos(0);
      setPhase('ready');
      void computeLanePeaks(res.stemUrls, res.emptyStems);
      // Chords run AFTER the rack is up, never before: the mixer is already
      // playable at this point, and the harmonic pass is a second model
      // download. Making the wait longer to arrive with more would trade the
      // one thing this page exists to deliver — hearing it split, fast.
      void analyzeChords(d.left.subarray(0, n), d.right.subarray(0, n), d.sampleRate);
      fx(`rl_splitter_${stage}_done`, {
        seconds: Math.round((Date.now() - started) / 1000),
        warm,
        dur: Math.round(d.durationSec),
      });
    } catch (e) {
      // A cancel is not a failure — it is the user getting what they asked
      // for. Go quietly back to the dropzone with no error strip.
      if ((e as Error)?.name === 'AbortError') {
        fx('rl_splitter_cancel', { stage, pct: Math.round(lastFracRef.current * 100), warm });
        return;
      }
      console.error('separation failed', e);
      fx('rl_splitter_error', { stage, reason: sepErrorReason(e), pct: Math.round(lastFracRef.current * 100) });
      setErr(
        String((e as Error)?.message || '').toLowerCase().includes('engine')
          ? "The audio engine couldn't load — check your connection and try again. Nothing was used up."
          : "That file couldn't be separated. Try another track. Nothing was used up.",
      );
      setPhase('error');
    } finally {
      if (abortRef.current === ac) abortRef.current = null;
    }
  }, [engine, isPro, user]);

  /**
   * Stop a run in flight. The worker is terminated, so this genuinely stops the
   * work rather than abandoning a promise and leaving the CPU pinned — which is
   * the only version of "Cancel" worth offering on a page whose whole promise
   * is that the work happens on your own machine.
   */
  function cancelRun() {
    abortRef.current?.abort();
    abortRef.current = null;
    engine.pause();
    setPlaying(false);
    setPhase('idle');
    setDecoded(null);
    setProg({ frac: 0 });
    setErr(null);
    sourceFileRef.current = null;
  }

  /**
   * The chord timeline for the window that was just separated. Analysis runs
   * over the whole window regardless of plan — the free ceiling is a DISPLAY
   * cut (see `chordSpanSec`), so a visitor who upgrades sees the rest of the
   * chords immediately rather than waiting for a second pass.
   *
   * Failures are swallowed on purpose: the mixer is the product here and it
   * already works. A model that didn't load is a missing nicety, not an error
   * worth interrupting someone's first minute with.
   */
  async function analyzeChords(left: Float32Array, right: Float32Array, sampleRate: number) {
    const run = ++chordRunRef.current;
    setChords(null);
    setChordKey(null);
    setChordsBusy(true);
    try {
      const segs = await detectChords(left, right, sampleRate);
      if (chordRunRef.current !== run) return; // a newer track took over
      // The chord timeline is a denoised harmonic analysis, so it reads the key
      // better than a chromagram would — and the key it gives back is what
      // decides sharp-vs-flat spelling below.
      const est = segs && segs.length ? keyFromChords(segs) : null;
      setChordKey(est);
      setChords(segs ?? []);
      fx('rl_splitter_chords_ready', { n: segs ? segs.length : 0 });
    } catch {
      if (chordRunRef.current === run) setChords(null);
    } finally {
      if (chordRunRef.current === run) setChordsBusy(false);
    }
  }

  // Decode each stem once more, off the blob URL, to draw its lane. Same
  // bucket density as the Studio (~10/sec, clamped) so the traces look
  // identical to '/'. A lane that fails just stays flat.
  async function computeLanePeaks(urls: Record<StemName, string>, empty: Set<StemName>) {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ac = new AC();
    try {
      for (const s of STEMS) {
        const url = urls[s];
        if (!url || empty.has(s)) continue;
        try {
          const buf = await ac.decodeAudioData(await (await fetch(url)).arrayBuffer());
          const left = buf.getChannelData(0);
          const right = buf.numberOfChannels > 1 ? buf.getChannelData(1) : left;
          const buckets = Math.round(Math.min(3200, Math.max(1600, buf.duration * 10)));
          setLanePeaks((prev) => ({ ...prev, [s]: computeEnvelope(left, right, buckets) }));
        } catch { /* one flat lane is not worth failing the mixer over */ }
      }
    } finally {
      try { await ac.close(); } catch { /* ignore */ }
    }
  }

  const onFile = useCallback(async (file: File) => {
    setErr(null);
    setFull(false);
    setPlaying(false);
    setDeclinedFree(false);
    // Drop the previous track's analysis now, not when the new one finishes —
    // otherwise the old ribbon sits under a new song for the whole wait.
    chordRunRef.current++;
    setChords(null);
    setChordKey(null);
    setChordsBusy(false);
    sourceFileRef.current = file;
    firstsRef.current = new Set();
    setPhase('decoding');
    fx('rl_splitter_upload', {
      source: arrivalRef.current,
      // Type and size only — never the name.
      ext: (file.name.match(/\.([a-z0-9]{1,6})$/i)?.[1] || '').toLowerCase(),
      mb: Math.round((file.size / 1048576) * 10) / 10,
    });
    try {
      const d = await decodeFileToStereo(file);
      const rec: Decoded = { ...d, name: file.name.replace(/\.[a-z0-9]+$/i, '') };
      setDecoded(rec);
      // THE POINT OF THIS PAGE: no goal chooser, no A/B picker, no account —
      // the first minute starts separating the moment the file is readable.
      await runSeparation(rec, PREVIEW_SECONDS);
    } catch (e) {
      console.error('decode failed', e);
      fx('rl_splitter_error', { stage: 'decode', reason: sepErrorReason(e) });
      setErr("That file couldn't be read. MP3, WAV, M4A and FLAC all work.");
      setPhase('error');
    }
  }, [runSeparation, isPro, user]);

  // Kept current so the mount-time handoff claim (which runs before this
  // callback's first identity settles) always calls the live version.
  useEffect(() => { onFileRef.current = onFile; }, [onFile]);

  function togglePlay() {
    if (playing) { engine.pause(); setPlaying(false); return; }
    // `play()`, NOT `playAll()` — the browser starts the AudioContext
    // suspended until a real gesture, and only play() resumes it. playAll()
    // silently started paused elements and the clock never moved.
    void engine.play();
    setPlaying(true);
    once('play', 'rl_splitter_play', { at: Math.round(pos) });
  }

  // Space toggles playback, the way every player does — the Studio's own rule,
  // verbatim. Text fields and selects keep the key; on a focused button we
  // preventDefault so the browser's space-activates-button doesn't fire a
  // second toggle. Two guards this page needs and the Studio doesn't: the
  // header keeps the key (space on the Tools button should open the menu, not
  // start the song), and the plan dialog swallows it entirely rather than
  // starting playback behind itself.
  useEffect(() => {
    if (phase !== 'ready' || !decoded || plansOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.code !== 'Space' && e.key !== ' ') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (t?.isContentEditable || tag === 'TEXTAREA' || tag === 'SELECT') return;
      // Ranges are the faders and the scrubber — space does nothing there, so
      // it may as well play. Every other input type keeps the key.
      if (tag === 'INPUT' && (t as HTMLInputElement).type !== 'range') return;
      if (t?.closest('.sp-top')) return;
      e.preventDefault();
      togglePlay();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, decoded, plansOpen, playing]);

  /** Mirrors `mix` synchronously so a burst of clicks compounds. Without it,
      every click in one tick reads the same render's `mix` and they collapse
      into a single step — measured: 20 rapid taps on the transpose stepper
      moved one semitone. */
  const mixRef = useRef(mix);
  mixRef.current = mix;

  function patchMix(next: MixState) {
    mixRef.current = next;
    setMix(next);
    engine.applyMix(next);
  }

  /** Relative moves must read the LATEST mix, not the render's — see mixRef. */
  function nudgeTranspose(delta: number) {
    once('tool:transpose', 'rl_splitter_tool', { tool: 'transpose' });
    const m = mixRef.current;
    patchMix({ ...m, transpose: Math.max(-12, Math.min(12, m.transpose + delta)) });
  }

  /** Back to the whole band at normal speed and pitch — everything the footer
      and the channel strips can change, in one click. */
  function resetMix() {
    once('tool:reset', 'rl_splitter_tool', { tool: 'reset' });
    patchMix({
      ...mix,
      speed: 1,
      transpose: 0,
      stems: Object.fromEntries(
        STEMS.map((s) => [s, { volume: 1, solo: false, mute: false }]),
      ) as MixState['stems'],
    });
  }

  function setStem(stem: StemName, patch: Partial<MixState['stems'][StemName]>) {
    const action = patch.mute !== undefined ? 'mute' : patch.solo !== undefined ? 'solo' : 'volume';
    once('mix:' + action, 'rl_splitter_mix', { action, stem });
    patchMix({ ...mix, stems: { ...mix.stems, [stem]: { ...mix.stems[stem], ...patch } } });
  }

  /**
   * The free doorway: carry this separation into the real Studio, where the
   * practice tools (loops, chords, speed, pitch, metronome) are free.
   *
   * Nothing large travels through the redirect — `/splitter` and `/` are the
   * same origin, so the stems go into the Studio's OWN cache and only a key
   * rides in the URL. StudioNext reads `?t=` and restores straight into the
   * mixer, so nothing re-separates and no credit is spent. Anonymous visitors
   * pass through /login?next=…, which carries the intent across OAuth; the
   * IndexedDB record is untouched by that round-trip.
   */
  async function openInStudio() {
    const urls = stemUrlsRef.current;
    if (!decoded || !urls || handingOff) return;
    setHandingOff(true);
    fx('rl_splitter_studio_handoff', { signed_in: !!user });
    markFunnel('splitter');
    try {
      const stems: Partial<Record<StemName, Blob>> = {};
      for (const s of STEMS) {
        if (emptyStems.has(s) || !urls[s]) continue;
        stems[s] = await (await fetch(urls[s])).blob();
      }
      const key = newSeparationKey();
      await cacheSeparation(key, {
        stems,
        emptyStems: [...emptyStems],
        // The Studio draws its own lane envelopes; these are the coarse bars
        // its rail and resume card read.
        peaks: computePeaks(decoded.left.subarray(0, Math.round(dur * decoded.sampleRate)),
          decoded.right.subarray(0, Math.round(dur * decoded.sampleRate)), 600),
        duration: dur,
        sourceRef: decoded.name,
        sourceTitle: decoded.name,
        // Carried so "pick a different section" works over there without a
        // re-upload — the same reason the Studio stores it on its own runs.
        source: sourceFileRef.current ?? undefined,
      });
      // The Studio logs `rl_splitter_studio_arrived` when it restores THIS key,
      // which closes the loop on whether the free doorway actually lands.
      try { sessionStorage.setItem('rl_splitter_handoff', key); } catch { /* ignore */ }
      const dest = `/?t=${encodeURIComponent(key)}`;
      window.location.href = user ? dest : `/login?next=${encodeURIComponent(dest)}`;
    } catch (e) {
      console.error('studio handoff failed', e);
      fx('rl_splitter_error', { stage: 'handoff_out' });
      setHandingOff(false);
      setErr('Could not open the mixer — try again.');
    }
  }

  function seekTo(t: number) {
    once('tool:seek', 'rl_splitter_tool', { tool: 'seek' });
    const clamped = Math.max(0, Math.min(dur || 0, t));
    engine.seekAll(clamped);
    setPos(clamped);
  }

  /**
   * Click or drag anywhere on the ruler (or on the playhead's flag) to move the
   * playhead — the Studio's own gesture, so the timeline behaves the same in
   * both places. Pointer capture keeps the drag alive when the cursor leaves
   * the ruler's 30px band, which is most of any real drag.
   */
  const rulerRef = useRef<HTMLDivElement>(null);
  const [scrubbing, setScrubbing] = useState(false);

  /**
   * The playhead is painted imperatively, not from React state — the same fix
   * the Studio needed. `pos` comes off the media element's `timeupdate`, which
   * fires ~4×/s: fine for the clock readout, visibly steppy on a moving needle.
   * While playing we poll the element clock from rAF and write straight to the
   * node, so the needle runs at display rate without re-rendering six lanes of
   * waveform sixty times a second.
   */
  const phRef = useRef<HTMLDivElement>(null);
  const paintPh = useCallback((t: number) => {
    const el = phRef.current;
    if (!el) return;
    el.style.left = `${Math.max(0, Math.min(100, (t / Math.max(0.01, dur)) * 100))}%`;
  }, [dur]);

  // Paused, scrubbing, or freshly seeked: `pos` is the source of truth.
  useEffect(() => {
    if (!playing) paintPh(pos);
  }, [pos, playing, paintPh]);

  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const tick = () => {
      paintPh(engine.currentTime);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, paintPh, engine]);
  function startScrub(e: React.PointerEvent) {
    const el = rulerRef.current;
    if (!el || !dur) return;
    e.preventDefault();
    const box = el.getBoundingClientRect();
    const at = (clientX: number) => seekTo(((clientX - box.left) / Math.max(1, box.width)) * dur);
    at(e.clientX);
    setScrubbing(true);
    const move = (ev: PointerEvent) => at(ev.clientX);
    const up = () => {
      setScrubbing(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  /** Back to the dropzone for another track. Frees the engine's current stems. */
  function newSong() {
    fx('rl_splitter_new_song', { phase });
    firstsRef.current = new Set();
    engine.pause();
    setPlaying(false);
    setPhase('idle');
    setDecoded(null);
    setLanePeaks({});
    setEmptyStems(new Set());
    setFull(false);
    setDeclinedFree(false);
    setErr(null);
    setPos(0);
    setDur(0);
    chordRunRef.current++;
    setChords(null);
    setChordKey(null);
    setChordsBusy(false);
    stemUrlsRef.current = null;
    sourceFileRef.current = null;
  }

  function applyMasterVol(v: number) {
    setMasterVol(v);
    engine.setMasterVolume(v);
  }

  function toggleMasterMute() {
    if (masterVol === 0) { applyMasterVol(preMuteVol || 1); return; }
    setPreMuteVol(masterVol);
    applyMasterVol(0);
  }

  // The one task button. What it does depends on who is asking — and only the
  // anonymous case is the experiment.
  async function onWholeSong() {
    if (!decoded) return;
    fx('rl_splitter_full_click', { dur: Math.round(decoded.durationSec) });
    // The whole song is what Pro buys — on this page free means the first
    // minute, signed in or not. An earlier version let a signed-in free user
    // spend 1 of 5 and run it, which flatly contradicted this page's own table
    // ("Separation length — Free: 60 seconds") and gave the modal nothing to
    // sell. Anonymous and free both get the offer; only Pro runs.
    if (!isPro) { openPlans('full_click'); return; }
    engine.pause();
    setPlaying(false);
    setFull(true);
    await runSeparation(decoded, null);
  }

  /* ── chord ribbon ────────────────────────────────────────────────────────
     A deliberately smaller port than the Studio's. That one also has a "chart"
     mode with paging, because a full-length song can carry 80 chords that will
     not fit a lane legibly. Here the window is 60 seconds at most, so the true
     timeline always fits and the ribbon can stay what it should be: a map of
     where the chords are, aligned to the lanes below it. */
  const chordSpanSec = isPro ? dur : Math.min(FREE_CHORDS_SECONDS, dur || FREE_CHORDS_SECONDS);
  const chordsGated = !isPro && dur > chordSpanSec + 0.5;

  // Fold the model's upper-structure blips into their neighbours (Fmaj7 heard
  // as Am, and so on) — the same clean-up the Studio applies before drawing.
  const displayChords = useMemo(
    () => (chords && chords.length
      ? simplifyChords(chords, undefined, chordKey ? { tonic: chordKey.tonic, mode: chordKey.mode } : null)
      : chords),
    [chords, chordKey],
  );

  const chordLaneRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = chordLaneRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setChordLaneW(el.getBoundingClientRect().width));
    ro.observe(el);
    setChordLaneW(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [phase, displayChords]);

  const [chordPage, setChordPage] = useState(0);

  /**
   * What to draw, in one of the Studio's two modes.
   *
   * TIME — block width IS the chord's duration, so the ribbon is a true map
   * aligned to the lanes. `hide` holds chords whose NAME would collide with the
   * next one: the block and its change edge stay, only the label drops, so a
   * couple of quick changes cost two names rather than the whole timeline.
   *
   * CHART — for songs whose harmonic rhythm is genuinely too fast to label on a
   * timeline (measured: a dense arrangement can put 30 changes in 30 seconds,
   * 13px apiece, and 27 of 30 names disappear). Even pitch, paged, following
   * the playhead. Losing the alignment is the price of being readable at all.
   */
  const chordStrip = useMemo(() => {
    if (!displayChords || !displayChords.length || !dur || chordLaneW <= 0) return null;
    const PITCH = 62;
    const MORE_W = 104;
    const useFlats = chordsPreferFlats(chordKey ? { tonic: chordKey.tonic, mode: chordKey.mode } : null, mix.transpose);
    const pool = displayChords
      .map((c, i) => ({ c, i, label: formatChord(c.label, mix.transpose, useFlats) }))
      .filter((x) => !!x.label && x.c.start < chordSpanSec) as { c: ChordSegment; i: number; label: string }[];
    if (!pool.length) return null;

    // Count the tight ones rather than requiring every chord to clear: one
    // quick change should not throw a whole readable song into chart mode.
    const tight = new Set<number>();
    pool.forEach((x, k) => {
      if (k === 0) return;
      const prev = pool[k - 1];
      const gapPx = ((x.c.start - prev.c.start) / dur) * chordLaneW;
      // ~7.4px per character at the lane's 12px mono, plus breathing room.
      if (gapPx < prev.label.length * 7.4 + 12) tight.add(prev.i);
    });
    if (tight.size <= Math.max(1, Math.round(pool.length * 0.25))) {
      return { mode: 'time' as const, items: pool, hide: tight, pitch: PITCH, page: 0, pages: 1, per: pool.length, paged: false, moreX: 0 };
    }

    const needsMore = pool.length * PITCH > chordLaneW || chordsGated;
    const per = Math.max(3, Math.floor((chordLaneW - (needsMore ? MORE_W : 0)) / PITCH));
    const pages = Math.max(1, Math.ceil(pool.length / per));
    const page = Math.min(chordPage, pages - 1);
    const items = pool.slice(page * per, page * per + per);
    // PITCH is a minimum, not a fixed cell: a last page holding three chords
    // should spread across the lane rather than bunch at the left.
    const avail = Math.max(PITCH, chordLaneW - (needsMore ? MORE_W : 0));
    const pitch = Math.min(PITCH * 2.6, Math.max(PITCH, avail / Math.max(1, items.length)));
    return {
      mode: 'chart' as const, items, hide: new Set<number>(), pitch, page, pages, per,
      paged: pages > 1,
      moreX: Math.min(items.length * pitch, Math.max(0, chordLaneW - MORE_W)),
    };
  }, [displayChords, chordKey, chordSpanSec, chordLaneW, dur, chordPage, chordsGated, mix.transpose]);

  // A page that never moved would show the intro while you play the chorus.
  useEffect(() => {
    if (!chordStrip || chordStrip.mode !== 'chart' || !chordStrip.paged || !displayChords) return;
    const useFlats = chordsPreferFlats(chordKey ? { tonic: chordKey.tonic, mode: chordKey.mode } : null, mix.transpose);
    const pool = displayChords
      .map((c, i) => ({ c, i, label: formatChord(c.label, mix.transpose, useFlats) }))
      .filter((x) => !!x.label && x.c.start < chordSpanSec);
    const rank = pool.findIndex((x) => pos >= x.c.start && pos < x.c.end);
    if (rank < 0) return;
    // `per` (the computed page SIZE), never `items.length` (the CURRENT page's
    // count). Using the latter feeds back: a short last page shrinks the
    // divisor, which selects an even higher page, which is shorter still — it
    // collapses to one chord per page within a couple of ticks.
    setChordPage(Math.floor(rank / Math.max(1, chordStrip.per)));
  }, [pos, chordStrip, displayChords, chordKey, chordSpanSec, mix.transpose]);

  /** The detected key, re-spelled at the current transpose. Built by feeding the
      tonic+mode back through the chord formatter, so the chip and the ribbon can
      never disagree about spelling (Eb vs D#) or about pitch. */
  const shownKey = useMemo(() => {
    if (!chordKey) return null;
    const flats = chordsPreferFlats({ tonic: chordKey.tonic, mode: chordKey.mode }, mix.transpose);
    return formatChord(`${chordKey.tonic}${chordKey.mode === 'minor' ? ':min' : ''}`, mix.transpose, flats);
  }, [chordKey, mix.transpose]);

  /** Which chord the playhead is inside. Chord changes are ~1/s, so the 4 Hz
      `pos` clock is plenty — this is the one thing that does NOT need rAF. */
  const curChord = useMemo(() => {
    if (!displayChords) return -1;
    for (let i = 0; i < displayChords.length; i++) {
      if (pos >= displayChords[i].start && pos < displayChords[i].end) return i;
    }
    return -1;
  }, [displayChords, pos]);

  const previewed = phase === 'ready' && !full && !!decoded && decoded.durationSec > PREVIEW_SECONDS;
  const busy = phase === 'decoding' || phase === 'separating';
  // Rescaled against the band this run actually traverses — see `progBase`.
  // `prog.frac` stays RAW everywhere else, because the status wording keys off
  // the engine's own checkpoints (SEP_BEGINS_AT) rather than the display.
  const pct = phase === 'decoding'
    ? 0
    : Math.round(Math.max(0, Math.min(1, (prog.frac - progBase) / (1 - progBase))) * 100);

  /**
   * A bar that always moves, without ever claiming more than has happened.
   *
   * The engine reports one separation step at a time and a step can run 15
   * seconds, so the raw number sits still and then jumps — measured on a real
   * run: 21% for sixteen seconds, then 27, 33, 39. Between reports this eases
   * toward the NEXT expected step at the rate the last steps actually ran, and
   * is hard-capped there, so it can creep but never overtake reality. It only
   * ever moves forward.
   */
  const [shownPct, setShownPct] = useState(0);
  const shownRef = useRef(0);
  const paceRef = useRef({ pct: 0, at: 0, gap: 6000, delta: 6 });

  useEffect(() => {
    const p = paceRef.current;
    if (pct === p.pct) return;
    const now = performance.now();
    // Learn this run's own rhythm from the last interval.
    if (p.at) { p.gap = Math.max(500, now - p.at); p.delta = Math.max(1, pct - p.pct); }
    p.pct = pct;
    p.at = now;
    shownRef.current = Math.max(shownRef.current, pct);
  }, [pct]);

  useEffect(() => {
    if (!busy) {
      shownRef.current = 0;
      paceRef.current = { pct: 0, at: 0, gap: 6000, delta: 6 };
      setShownPct(0);
      return;
    }
    // Seed the warm-up: nothing is reported until the engine session exists,
    // which is several seconds of apparent nothing. Creep toward 20 — under the
    // first real checkpoint — so the bar is alive from the first frame.
    if (!paceRef.current.at) paceRef.current = { pct: 0, at: performance.now(), gap: 9000, delta: 20 };
    const id = setInterval(() => {
      const p = paceRef.current;
      const ceiling = Math.min(99, p.pct + p.delta);
      const t = performance.now() - p.at;
      // Asymptotic, not linear: a linear ease reaches the ceiling and then sits
      // there — measured, 14 seconds frozen at 27% when one step ran long. This
      // approaches the ceiling and never arrives, so a slow step still shows
      // movement while remaining incapable of overtaking the real progress.
      const eased = p.pct + (ceiling - p.pct) * (1 - Math.exp(-t / p.gap));
      shownRef.current = Math.max(shownRef.current, Math.min(eased, ceiling));
      setShownPct(Math.round(shownRef.current));
    }, 200);
    return () => clearInterval(id);
  }, [busy]);

  /**
   * The tab title tracks the page's state, because the wait here is long enough
   * that people switch away during it. The percentage leads: tabs truncate from
   * the RIGHT, so a narrow one still shows "72% · Spli…", which is the whole
   * point of putting it there.
   *
   * The idle and ready lines echo the H1 and the result heading, so the tab and
   * the page can never disagree. "Riffloop" rather than "Riffloop Studio" — an
   * anonymous visitor arriving from an article has not met the Studio yet.
   *
   * Restores whatever was there on unmount: this page is one route inside the
   * SPA, and leaving a stale progress figure on /login or the Studio would be
   * worse than never having set it.
   */
  useEffect(() => {
    const previous = document.title;
    return () => { document.title = previous; };
  }, []);

  useEffect(() => {
    document.title =
      phase === 'ready' ? 'Your previews are ready · Riffloop'
      : phase === 'error' ? "That didn't work · Riffloop"
      : busy ? `${shownPct}% · Splitting your song · Riffloop`
      : 'Hear any song split apart · Riffloop';
  }, [phase, busy, shownPct]);

  /**
   * What the wait says. Two reasons it is staged rather than one fixed line:
   * a first visit spends its opening stretch on set-up, and claiming to be
   * working on their file before that finishes would be a lie; and a single
   * frozen sentence for a minute reads as a hang, which is when people leave.
   * `sepElapsed` counts only from the moment real separation starts, so the
   * ladder isn't spent while the set-up is still running.
   */
  function statusLine(): string {
    if (phase === 'decoding') return 'Reading your track…';
    if (prog.frac < SEP_BEGINS_AT || sepStartedRef.current == null) return 'Getting things ready…';
    const s = sepElapsed;
    if (s < 10) return 'Working on your file…';
    if (s < 22) return 'Listening to the mix…';
    if (s < 38) return 'Finding each instrument…';
    if (s < 60) return 'Pulling the parts apart…';
    return 'Nearly there…';
  }

  // `is-pinned` on the result screen: the header, the player and the offer strip
  // stay on screen and the LANES give instead — see splitter.css. Without it the
  // strip fell below the fold on a short window, which is the one thing on this
  // page that must never happen.
  return (
    <div className={`sxr sp-page${phase === 'ready' ? ' is-pinned' : ''}`}>
      <SplitterNav />
      {/* One scroll container between the fixed header and the docked player —
          the Studio's shape exactly. Without it the page scrolled as a whole
          document, which left the player floating a screen below the last row
          on anything but a short window. */}
      <div className="sp-scroll">

      {phase === 'idle' && (
        <section className="sp-hero">
          <h1>Hear any song split apart.</h1>
          <p className="sp-sub">
            Drop a track and the first {PREVIEW_SECONDS} seconds are separated into{' '}
            {phone ? 'two parts' : 'six parts'}. Free, and no account needed.
          </p>
          {/* The channels this device can fill, named and coloured exactly as
              the rack will show them. Says what "split apart" means before the
              upload rather than after — which on a phone means TWO chips, not
              six: the 2-stem engine cannot produce the other four, and printing
              them here made the rack that arrives look broken. */}
          <div className="sp-stems" aria-hidden="true">
            {shownStems.map((s) => (
              <span className="sp-stemchip" key={s.id}>
                <i style={{ background: s.color }} />{stemName(s.id as StemName, s.name)}
              </span>
            ))}
          </div>
          <label
            className={`sp-drop${dragOver ? ' over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault(); setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) void onFile(f);
            }}
          >
            <input
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.flac,.ogg"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); }}
            />
            <span className="sp-drop-i" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 16V4" /><path d="M7.5 8.5 12 4l4.5 4.5" /><path d="M4 19h16" />
              </svg>
            </span>
            <span className="sp-drop-t">Drop a song here, or click to choose</span>
            <span className="sp-drop-s">MP3 · WAV · M4A · FLAC · up to 50 MB</span>
          </label>
          {/* The two things people actually want to know before handing over a
              file, and both are true: the audio is separated in this browser,
              and the free minute costs nothing and asks for nothing. */}
          <p className="sp-hero-note">
            Your audio never leaves your device · no account, no card
          </p>
          {/* Stated BEFORE the work, which is this page's own rule — a phone
              user learning about the 2-stem engine from a half-empty rack three
              minutes later is exactly the discovery-after-the-fact this page
              exists to avoid. The link is offered here too so nobody has to sit
              through a phone separation to find out they wanted a computer. */}
          {phone && (
            <ContinueOnDesktop
              variant="inline"
              handoff={{ kind: 'file' }}
              title="Six stems need a computer"
              body={`On a phone Riffloop splits a song into ${MOBILE_STEM_COUNT} parts — vocals and instrumental. All ${DESKTOP_STEM_COUNT} — drums, bass, piano, guitar and lead — need a computer.`}
              shareText="Open this on your computer to split a song into all 6 stems on Riffloop."
              onShown={() => fx('rl_splitter_desktop_shown', { where: 'idle', kind: 'file' })}
              onSent={(how) => fx('rl_splitter_desktop_send', { where: 'idle', how, kind: 'file' })}
            />
          )}
        </section>
      )}

      {busy && (
        <section className="sp-work">
          {/* A skeleton of the rack the wait is building, so the screen shows
              the shape of what's coming rather than a bar on an empty page —
              the Studio does the same while it hydrates. */}
          <div className="sp-work-card">
            <SplitLoader />
            {/* The engine's own progress label is deliberately NOT shown — it
                describes where the work happens, which is not the user's
                business. Ours moves through stages instead, so a long wait
                never looks like a stuck one. */}
            <div className="sp-work-t">{statusLine()}</div>
            <p className="sp-work-s">
              {full
                ? 'The whole song — this one takes longer.'
                : `First ${PREVIEW_SECONDS} seconds`}
              {phone && ' · vocals and instrumental'}
            </p>
            {/* The bar carries the number INSIDE it, as in the reference. While
                decoding there is no real progress to report — a local file is
                read in milliseconds and nothing streams — so it runs
                indeterminate rather than parking on a made-up percentage. */}
            {phase === 'decoding' ? (
              <div className="sp-fbar is-indet" aria-label="Reading your track" />
            ) : (
              <div className="sp-fbar">
                <i style={{ width: `${shownPct}%` }} />
                <span>{shownPct}%</span>
              </div>
            )}
            {/* A real way out of a long wait, as in the reference. It genuinely
                stops the work — see cancelRun — rather than hiding a run that
                keeps the machine pinned. */}
            <button className="sp-cancel" onClick={cancelRun}>Cancel</button>
          </div>
          {/* The Studio's OWN loader, not a lookalike: the real rack markup
              wearing `.sx-rack.hydrating`, so it inherits that page's grayscale,
              unfilled lanes and 1.9s `sx-breathe` verbatim. Because it is the
              same element the finished rack is, the geometry is identical and
              nothing shifts when the stems land — the rack simply gains colour,
              which is the completion signal at '/' too. */}
          <div className="sp-rack sx-rack hydrating" aria-hidden="true">
            <div className="sx-rack-head">
              <span className="sx-lbl">Channels</span>
              <span className="sp-rack-hint">Mute or solo any part — the rest keeps playing</span>
            </div>
            <div className="sx-tracks">
              <div className="sx-ch sx-ruler-row"><div /><div className="sx-ruler" /></div>
              {/* `shownStems`, not STEM_UI: the skeleton is a promise about the
                  rack that is coming, and on a phone four of those six lanes
                  arrive silent. Raising six and delivering two reads as a
                  failure rather than as the device's limit. */}
              {shownStems.map((s) => (
                <div className="sx-ch sx-stem-row" key={s.id}>
                  <div className="sx-strip" style={{ ['--mc' as string]: s.color }}>
                    <div className="sx-strip-top">
                      <span className="sx-ms">M</span>
                      <span className="sx-ms">S</span>
                      <span className="sx-strip-n">{stemName(s.id as StemName, s.name)}</span>
                      <span className="sx-pct sx-mono">100%</span>
                    </div>
                    <input className="sx-vol" type="range" min={0} max={100} value={100} readOnly tabIndex={-1} />
                  </div>
                  <div className="sx-lane"><span className="sx-lane-mid" /></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {phase === 'error' && (
        <section className="sp-work">
          <div className="sp-work-card">
            <div className="sp-work-t">Didn't work</div>
            <p className="sp-work-s">{err}</p>
            <button className="sp-cta" onClick={() => { setPhase('idle'); setErr(null); }}>Try another file</button>
          </div>
        </section>
      )}

      {phase === 'ready' && decoded && (
        <section className="sp-result">
          {/* The Studio's own result header — thumb, 19px title, tool chips on
              the right. Same parts in the same order as the mixer at '/', so
              the handoff between the two pages doesn't change the furniture. */}
          <div className="sx-rhead sp-rhead">
            <div className="sx-thumb sm" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18V6l10-2v12" />
                <circle cx="6.5" cy="18" r="2.5" /><circle cx="16.5" cy="16" r="2.5" />
              </svg>
            </div>
            {/* The heading is the STATUS, the song is its subtitle — the
                reference's hierarchy. It costs no extra height because it
                reuses the two lines the song header already had, which matters:
                every fixed pixel here comes out of the lanes' budget. */}
            <div className="sp-rtitlewrap">
              <div className="sx-rtitle">
                {full ? 'Your whole song is ready!' : 'Your previews are ready!'}
              </div>
              <div className="sp-rsub">
                {decoded.name} · {full
                  ? `whole song, ${fmtTime(decoded.durationSec)}`
                  : `first ${PREVIEW_SECONDS} seconds of ${fmtTime(decoded.durationSec)}`}
                {/* The rack shows two lanes on a phone and hides the four
                    silent ones, which without this reads as four stems having
                    gone missing rather than as what this device produces. */}
                {twoStem && ` · ${MOBILE_STEM_COUNT} parts on this device`}
              </div>
            </div>
            <span className="sx-spacer" />
            {/* States the cap where the work is, not only in the strip below —
                the rule this page runs on is that the limit is visible before
                anyone asks for more, never discovered afterwards. */}
            {!full && (
              <span className="sp-chip lead" title={`Free separations cover the first ${PREVIEW_SECONDS} seconds`}>
                Free · first {PREVIEW_SECONDS}s
              </span>
            )}
            <span className="sp-chip sx-mono">{fmtTime(pos)} / {fmtTime(dur)}</span>
            <button className="sx-tool sp-newsong" onClick={newSong} title="Separate a different song">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 16V4" /><path d="M7.5 8.5 12 4l4.5 4.5" /><path d="M4 19h16" />
              </svg>
              New song
            </button>
          </div>

          {/* The Studio's OWN rack markup and classes — same strip, same fader,
              same tape lane — minus the practice tools (presets, chords, loops,
              speed, transpose, pan). Those are what the full Studio adds; if
              the preview handed them over too there would be nothing left to
              click the button for. */}
          {/* ── TRANSPORT ─────────────────────────────────────────────────
              The Studio's own player bar (.sx-transport / .sx-tp-row /
              .sx-scrub), minus the Chords and Loops panel toggles — those
              open panels this page doesn't have. It sits ABOVE the lanes,
              following the reference layout: play and clock first, the
              waveforms under them, the ask last (owner's call, 2026-09-01). */}
          <div className="sx-transport sp-transport">
            <div className="sx-tp-row">
              <div className="sx-volwrap">
                <button
                  className={`sx-tbtn${masterVol === 0 ? ' off' : ''}`}
                  onClick={toggleMasterMute}
                  title={masterVol === 0 ? 'Unmute' : 'Mute everything'}
                  aria-label={masterVol === 0 ? 'Unmute' : 'Mute everything'}
                >
                  <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 9.3h3.2L11.4 5.6v12.8L7.2 14.7H4z" />
                    {masterVol === 0
                      ? <path d="M15.6 9.8l4.8 4.4M20.4 9.8l-4.8 4.4" />
                      : <><path d="M15.2 9.5a3.6 3.6 0 0 1 0 5" /><path d="M18 7.2a7.2 7.2 0 0 1 0 9.6" /></>}
                  </svg>
                </button>
                <input
                  className="sx-volpop"
                  type="range" min={0} max={100} value={Math.round(masterVol * 100)}
                  onChange={(e) => applyMasterVol(Number(e.target.value) / 100)}
                  aria-label="Output volume"
                />
              </div>
              <button className="sx-tbtn" onClick={() => seekTo(pos - 10)} title="Back 10s" aria-label="Back 10 seconds">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="M12.4 12 21 6.3v11.4zM3 12l8.6-5.7v11.4z" /></svg>
              </button>
              <button className="sx-tbtn play" onClick={togglePlay} title="Play / pause (Space)" aria-label={playing ? 'Pause' : 'Play'}>
                {playing
                  ? <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true"><path d="M9.3 4.8v14.4M14.7 4.8v14.4" /></svg>
                  : <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true"><path d="M6.5 4.2 20 12 6.5 19.8z" /></svg>}
              </button>
              <button className="sx-tbtn" onClick={() => seekTo(pos + 10)} title="Forward 10s" aria-label="Forward 10 seconds">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="M11.6 12 3 17.7V6.3zM21 12l-8.6 5.7V6.3z" /></svg>
              </button>
              <button
                className={`sx-tbtn${mix.loop ? ' on' : ''}`}
                onClick={() => { once('tool:loop', 'rl_splitter_tool', { tool: 'loop' }); patchMix({ ...mix, loop: !mix.loop }); }}
                title={mix.loop ? 'Looping' : 'Play straight through'}
                aria-label="Loop"
                aria-pressed={mix.loop}
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M14.25 15.08A6.16 6.16 0 1 1 12.36 6.9" />
                  <path d="M8.92 3.2 12.36 6.9" />
                  <path d="M9.75 8.92A6.16 6.16 0 1 1 11.64 17.1" />
                  <path d="M15.08 20.8 11.64 17.1" />
                </svg>
              </button>
            </div>
            <div className="sx-scrub">
              <span className="sx-mono">{fmtTime(pos)}</span>
              <input
                type="range" min={0} max={Math.max(0.01, dur)} step={0.01} value={pos}
                onChange={(e) => seekTo(Number(e.target.value))}
                aria-label="Seek"
              />
              <span className="sx-mono dim">{fmtTime(dur)}</span>
            </div>
          </div>

          {/* Same classes the loading rack carries, minus `.hydrating` — so the
              two are literally one element in two states, as at '/'. */}
          <div className="sp-rack sx-rack">
            {/* The rack's own header row, like the Studio's. There the pills are
                mix presets; here they name what the six lanes are, and the
                right-hand tag says how much of the song is on them. */}
            <div className="sx-rack-head">
              <span className="sx-lbl">Channels</span>
              <span className="sp-rack-hint">Mute or solo any part — the rest keeps playing</span>
              <span className="sx-spacer" />
              <span className="sp-rack-len sx-mono">{fmtTime(dur)}</span>
            </div>

            <div className="sx-tracks">
              {/* Ruler + lanes share one positioning context so a single
                  playhead spans the whole rack, exactly as at '/'. */}
              <div className="sx-ch sx-ruler-row">
                <div />
                <div
                  className="sx-ruler"
                  ref={rulerRef}
                  onPointerDown={startScrub}
                  title="Click or drag to move the playhead"
                />
              </div>

              {/* ── CHORDS ────────────────────────────────────────────────
                  Sits between the ruler and the lanes, on the same grid, so a
                  chord block lines up with the audio underneath it. Blocks are
                  true to time — width IS the chord's duration — and clicking
                  one moves the playhead there. */}
              {(chordsBusy || (chords && chords.length >= 0)) && (
                <div className="sx-ch sx-chord-row">
                  <div className="sx-chordstrip">
                    <span className="sx-strip-n">Chords</span>
                    {/* Transposed with the chords. A key chip that stayed put
                        while every chord under it moved would contradict the
                        ribbon it labels — so it is re-spelled through the same
                        formatter, from the detected tonic and mode. */}
                    {chordKey && shownKey && (
                      <span
                        className="sp-chordkey sx-mono"
                        title={mix.transpose
                          ? `Key, read from the chord timeline — transposed ${mix.transpose > 0 ? '+' : ''}${mix.transpose} semitones`
                          : 'Key, read from the chord timeline'}
                      >
                        {chordKey.confidence < 0.5 ? '~' : ''}{shownKey}
                      </span>
                    )}
                    {/* The ceiling is a fact about the plan, not a place in the
                        song, so it is stated here from the start rather than
                        left to be discovered where the ribbon stops. */}
                    {chordsGated && (
                      <button
                        className="sx-chordfree"
                        onClick={() => openPlans('chords')}
                        title={`Chords cover the first ${FREE_CHORDS_SECONDS} seconds on the free plan — Pro reads the whole song`}
                      >
                        <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="4" y="10.5" width="16" height="10.5" rx="2.4" />
                          <path d="M8 10.5V7a4 4 0 018 0v3.5" />
                        </svg>
                        First {FREE_CHORDS_SECONDS}s
                      </button>
                    )}
                  </div>
                  <div className="sx-chordlane" ref={chordLaneRef}>
                    {chordsBusy && !chords && <span className="sx-chord-wait">Listening for chords…</span>}
                    {!chordsBusy && chords && !chordStrip && (
                      <span className="sx-chord-wait">No chords heard in this section</span>
                    )}
                    {chordStrip?.mode === 'time' && chordStrip.items.map((x) => {
                      const end = Math.min(x.c.end, chordSpanSec);
                      const w = ((end - x.c.start) / dur) * 100;
                      if (w < 0.4) return null;
                      return (
                        <button
                          key={x.i}
                          className={`sx-chordblk sx-mono${curChord === x.i ? ' cur' : ''}`}
                          style={{ left: `${(x.c.start / dur) * 100}%`, width: `${w}%` }}
                          onClick={() => seekTo(x.c.start + 0.01)}
                          title={`${x.label} — jump to ${fmtTime(x.c.start)}`}
                        >
                          {!chordStrip.hide.has(x.i) && <span>{x.label}</span>}
                        </button>
                      );
                    })}
                    {chordStrip?.mode === 'chart' && chordStrip.items.map((x, j) => (
                      <button
                        key={x.i}
                        className={`sx-chordblk sx-chordblk-fix sx-mono${curChord === x.i ? ' cur' : ''}`}
                        style={{ left: `${j * chordStrip.pitch}px`, width: `${chordStrip.pitch}px` }}
                        onClick={() => seekTo(x.c.start + 0.01)}
                        title={`${x.label} — jump to ${fmtTime(x.c.start)}`}
                      >
                        <span>{x.label}</span>
                      </button>
                    ))}
                    {/* Sits where the chords stop, so the cut explains itself
                        rather than reading as the ribbon having run out. */}
                    {chordStrip?.mode === 'chart' && (chordStrip.paged || chordsGated) && (
                      <div className="sx-chordpager" style={{ left: `${chordStrip.moreX}px` }}>
                        {chordsGated ? (
                          <button
                            className="sx-chordpage-m locked"
                            onClick={() => openPlans('chords')}
                            title={`Chords stop at ${FREE_CHORDS_SECONDS} seconds on the free plan — Pro reads the whole song`}
                          >
                            <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <rect x="4" y="10.5" width="16" height="10.5" rx="2.4" />
                              <path d="M8 10.5V7a4 4 0 018 0v3.5" />
                            </svg>
                            See more
                          </button>
                        ) : (
                          <span className="sx-chordpage-m" title="Page follows the playhead">
                            {chordStrip.page + 1}/{chordStrip.pages}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Time mode only: the padlock marks a real point on the
                        timeline, which a chart page has no equivalent of. */}
                    {chordsGated && chordStrip?.mode === 'time' && (
                      <button
                        className="sx-lockedtail"
                        style={{ left: `${(chordSpanSec / Math.max(0.01, dur)) * 100}%` }}
                        onClick={() => openPlans('chords')}
                        aria-label={`Chords stop after ${FREE_CHORDS_SECONDS} seconds — Pro reads the whole song`}
                        title="Pro — chords for the whole song"
                      >
                        <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="4" y="10.5" width="16" height="10.5" rx="2.4" />
                          <path d="M8 10.5V7a4 4 0 018 0v3.5" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {STEM_UI.map((s, si) => {
                const st = mix.stems[s.id as StemName];
                const empty = emptyStems.has(s.id as StemName);
                const anySolo = STEMS.some((x) => mix.stems[x].solo);
                const dim = empty || audibleVolume(st, anySolo) <= 0.005;
                const env = lanePeaks[s.id as StemName];
                return (
                  <div key={s.id} className={`sx-ch sx-stem-row${empty ? ' empty' : ''}`}>
                    <div className={`sx-strip${dim ? ' dim' : ''}`} style={{ ['--mc' as string]: s.color }}>
                      <div className="sx-strip-top">
                        <button
                          className={`sx-ms${st.mute ? ' m-on' : ''}`}
                          onClick={() => setStem(s.id as StemName, { mute: !st.mute })}
                          title="Mute"
                        >M</button>
                        <button
                          className={`sx-ms${st.solo ? ' s-on' : ''}`}
                          onClick={() => setStem(s.id as StemName, { solo: !st.solo })}
                          title="Solo"
                        >S</button>
                        {/* A 2-stem run's `other` lane holds the WHOLE
                            instrumental, so the app's usual "Solo & Lead"
                            names it as if the drums and guitar were missing
                            from a lane that contains both. */}
                        <span className="sx-strip-n">{stemName(s.id as StemName, s.name)}{empty && <i className="sx-silent"> · silent</i>}</span>
                        <span className="sx-pct sx-mono">{Math.round(st.volume * 100)}%</span>
                      </div>
                      <input
                        className="sx-vol"
                        type="range" min={0} max={100} value={Math.round(st.volume * 100)}
                        onChange={(e) => setStem(s.id as StemName, { volume: Number(e.target.value) / 100 })}
                        aria-label={`${stemName(s.id as StemName, s.name)} volume`}
                      />
                    </div>
                    {/* Same ramp as the live rack: one hue, 12% lighter per
                        lane, so the six read as a single gradient down the
                        rack. `--laneTone` is a lightness step, not a colour. */}
                    {/* Each lane carries its own loading state until ITS
                        envelope lands — the stems decode one at a time, so a
                        whole-rack treatment would call four finished lanes
                        unfinished. `empty` is excluded or a genuinely silent
                        stem would sweep for ever, waiting on a wave that is
                        never coming. */}
                    <div
                      className={`sx-lane${dim ? ' dim' : ''}${!env && !empty ? ' sp-lane-wait' : ''}`}
                      style={{ ['--laneTone' as string]: `${si * 12}%` }}
                    >
                      {env ? <LaneWave env={env} /> : <span className="sx-lane-mid" />}
                    </div>
                  </div>
                );
              })}

              {/* ONE playhead for the rack rather than six per-lane marks: a
                  ghost copy of the .sx-ch grid, so its lane column lands on the
                  lanes without anything being measured. The flag is draggable
                  and the hairline drops through every channel — the Studio's
                  own scrubber, same markup. */}
              <div className="sx-phwrap">
                <div />
                <div className="sx-phcol">
                  {/* No `left` from React — `paintPh` owns this node's position
                      so the needle can move every frame. */}
                  <div className={`sx-ph${scrubbing ? ' grabbing' : ''}`} ref={phRef}>
                    <span className="sx-ph-flag" onPointerDown={startScrub} />
                  </div>
                </div>
              </div>
            </div>

            {/* ── SPEED + KEY ───────────────────────────────────────────────
                The Studio's own rack footer, same controls and same classes.
                Both are FREE — the plan table on this very page says "speed &
                pitch" are free on both tiers, so gating them here would make
                this page contradict its own modal. Transpose re-spells the
                chord ribbon above it; speed changes tempo only (SoundTouch
                undoes the pitch shift that a playback-rate change would cause).
                If the pitch worklet failed to load, transpose no-ops and speed
                still works — same fallback as '/'. */}
            <div className="sx-rack-foot">
              <button className="sx-reset" onClick={resetMix}>Reset</button>
              <span className="sx-legend"><b>M</b> mute · <b>S</b> solo</span>
              <span className="sx-spacer" />
              <div className="sx-speedwrap">
                <span className="sx-step-l">Speed</span>
                {[0.5, 0.75, 1, 1.25, 1.5].map((v) => (
                  <button
                    key={v}
                    className={`sx-speed sx-mono${mix.speed === v ? ' on' : ''}`}
                    onClick={() => { once('tool:speed', 'rl_splitter_tool', { tool: 'speed', value: v }); patchMix({ ...mix, speed: v }); }}
                  >
                    {v}×
                  </button>
                ))}
              </div>
              <div className="sx-step">
                <span className="sx-step-l">Key</span>
                <button className="sx-step-b" onClick={() => nudgeTranspose(-1)} aria-label="Down a semitone">−</button>
                <span className="sx-step-v sx-mono">{mix.transpose > 0 ? '+' : ''}{mix.transpose} st</span>
                {/* No range hint: the steppers clamp at ±12 anyway, so it was
                    stating a limit nobody can cross rather than telling anyone
                    anything. */}
                <button className="sx-step-b" onClick={() => nudgeTranspose(1)} aria-label="Up a semitone">+</button>
              </div>
            </div>
          </div>


          {/* The task row has two states. Before any answer it asks for the
              whole song. After "keep using free" it becomes the FREE doorway —
              in place, not as a second popup chasing a decline: the "no" is
              honoured, the modal just closes, and what they get instead is a
              standing offer they can take whenever. The paid path stays
              available underneath rather than disappearing. */}
          {previewed && !declinedFree && (
            /* The reference's shape, taken the rest of the way: the button IS
               the strip. No card, no paragraph — one big ask sitting right
               under the waveforms it refers to.
               ⚠️ The free doorway ("keep this minute, open the full mixer")
               used to live here and now only exists inside the plan modal's
               exits. That was the owner's call; if the modal's decline rate
               moves, this is the change to look at first. */
            <button className="sp-cta sp-cta-hero" onClick={() => void onWholeSong()}>
              Split in Full →
            </button>
          )}
          {previewed && declinedFree && (
            /* They said "keep using free", so the big button becomes the free
               doorway. The paid path keeps ONE quiet line under it — without
               that there is no way back to the offer at all, which would make
               the decline irreversible. */
            <div className="sp-ctastack">
              <button className="sp-cta sp-cta-hero" onClick={() => void openInStudio()} disabled={handingOff}>
                {handingOff ? 'Opening…' : 'Open the full mixer →'}
              </button>
              <button className="sp-next-alt" onClick={() => void onWholeSong()}>
                Or split the whole song
              </button>
            </div>
          )}
          {full && (
            <Link className="sp-cta sp-cta-hero" to={user ? '/' : '/login'}>
              {user ? 'Open in the Studio →' : 'Sign in to keep it →'}
            </Link>
          )}
          {/* Under the ask, not over it: the paid path is still this page's
              first offer, and this is the one honest answer to "where are my
              other four stems". Rendered on a phone whatever the plan — Pro
              buys the whole song, not a bigger engine on this device. */}
          {phone && (
            <ContinueOnDesktop
              handoff={{ kind: 'file' }}
              title={`Want all ${DESKTOP_STEM_COUNT} stems?`}
              body={`This phone splits into ${MOBILE_STEM_COUNT} parts — vocals and instrumental. Drums, bass, piano, guitar and lead are separated on a computer. Send yourself the link and pick the same file up there.`}
              shareText="Open this on your computer to split a song into all 6 stems on Riffloop."
              onShown={() => fx('rl_splitter_desktop_shown', { where: 'ready', kind: 'file' })}
              onSent={(how) => fx('rl_splitter_desktop_send', { where: 'ready', how, kind: 'file' })}
            />
          )}
        </section>
      )}

        {/* Compact on the result screen: the second line of legal links costs
            43px of the lane budget, and every pixel spent on chrome there is a
            pixel closer to a scrollbar inside the rack. The rights sentence —
            the one that has to be there — stays. */}
        <Footer compact={phase === 'ready'} />
      </div>{/* /.sp-scroll */}

      {/* The ask. LALAL's shape — task-framed title, annual anchored, free named
          last by what it is — with the one thing they leave out: a true way
          through for people who already have an account. */}
      {/* ── UPGRADE MODAL ─────────────────────────────────────────────────
          Deliberately the EXTENSION's upgrade modal, rebuilt in React: same
          760px card, same header gradient and 30px hero, same three reason
          tiles, same two plan cards with floating badges, same billing pills
          and gradient CTA. One offer surface across both surfaces — a user who
          saw it on YouTube meets the same thing here.
          Dark in BOTH themes, like /login: it is a lit surface of its own, and
          the extension has no light variant to match. */}
      {plansOpen && (
        <div
          className="sp-up-scrim"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sp-up-t"
          onClick={(e) => { if (e.target === e.currentTarget) closePlans('backdrop'); }}
        >
          <div className="sp-up-card">
            <div className="sp-up-head">
              <div className="sp-up-headrow">
                <Logo size="sm" />
                <span className="sp-up-spacer" />
                <button className="sp-up-x" onClick={() => closePlans('x')} aria-label="Close">✕</button>
              </div>
              <div className="sp-up-hero" id="sp-up-t">Unlock the full song</div>
              <p className="sp-up-sub">
                The first {PREVIEW_SECONDS} seconds stay free. Pro is the whole track, every track.
              </p>
              {/* LALAL puts this on their plan page for the same reason: an
                  existing customer must not have to buy again to get past a
                  wall. `?next=` carries them back here after sign-in. */}
              {/* Anonymous visitors only. A signed-in free user reaches this
                  modal too (quota exhausted), and telling them to log in would
                  be both wrong and faintly insulting. */}
              {!user && (
                <p className="sp-up-signin">
                  Already have an account?{' '}
                  <Link to={`/login?next=${encodeURIComponent('/splitter')}`} onClick={() => { markFunnel('splitter'); fx('rl_splitter_plan_click', { plan: 'signin' }); }}>
                    Log in
                  </Link>
                </p>
              )}
            </div>

            {/* The whole feature set, Free beside Pro. Every row is a shipped
                capability with its REAL gate — most of the practice tools are
                free and stay free, and saying so is what makes the paid rows
                believable. (Lyrics are deliberately absent: they exist only in
                the extension's karaoke mode and are ungated there, so listing
                them as a Studio Pro feature would be a lie.) */}
            {/* ⚠️ LOAD-BEARING WRAPPER. The card is a flex COLUMN capped at
                `calc(100vh - 40px)` with `overflow: hidden`, and its children
                had no `flex` of their own — so on any short window they were
                SQUASHED rather than scrolled, and `.sp-up-table` (itself
                `overflow: hidden`, for its rounded corners) silently ate four
                of its seven rows. Measured at 375×812: 157px shown of 304px,
                with "Export stems" and "Separation quality" invisible. This
                wrapper is the part that scrolls; the header and the CTA stay
                put, so the offer is never the thing that gets cut. */}
            <div className="sp-up-body">
            <div className="sp-up-table">
              <div className="sp-up-trow sp-up-thead">
                <span>What you get</span><span>Free</span><span>Pro</span>
              </div>
              {[
                // The headline difference — the reason this modal exists. It
                // carries its own weight class so the table reads as
                // confirmation of a decision, not as the argument for one.
                ['Separation length', `${PREVIEW_SECONDS} seconds`, 'Whole song', true],
                ['Chords & beat grid', 'First 30s', 'Whole song'],
                ['Metronome / click', 'First 30s', 'Whole song'],
                ['Saved loops', '3 per song', 'Unlimited'],
                ['Export stems', '—', 'MP3 · WAV · M4A'],
                ['Separation quality', 'Standard', 'Best'],
                ['On YouTube (extension)', '60s loops', '5-min loops'],
              ].map(([label, free, pro, lead]) => (
                <div className={`sp-up-trow${lead ? ' lead' : ''}`} key={label as string}>
                  <span>{label}</span>
                  <span className="sp-up-tfree">{free}</span>
                  <span className="sp-up-tpro">{pro}</span>
                </div>
              ))}
            </div>
            <div className="sp-up-also">
              Free on both: {phone ? 'vocals & instrumental' : 'six stems'}, the full mixer,
              key &amp; tempo detection, speed &amp; pitch.
              {/* Pro buys the whole SONG, not a bigger engine on this device —
                  saying "six stems" to a phone would sell something no plan
                  here delivers. */}
              {phone && ` All ${DESKTOP_STEM_COUNT} stems are separated on a computer, on any plan.`}
            </div>

            <div className="sp-up-plans">
              {/* The cycle switch sits ABOVE the cards: it CHANGES the price
                  printed on the Pro card, so meeting it afterwards means
                  re-reading a number you had already taken in. */}
              {upPlan !== 'lifetime' && (
                <div className="sp-up-billing">
                  {(['monthly', 'yearly'] as const).map((k) => (
                    <button
                      key={k}
                      className={`sp-up-pill${upBilling === k ? ' on' : ''}`}
                      onClick={() => { setUpBilling(k); fx('rl_splitter_modal_toggle', { what: 'billing', value: k }); }}
                    >
                      {k === 'monthly' ? 'Monthly' : 'Yearly'}
                    </button>
                  ))}
                  {/* Always rendered, not only once yearly is picked — as a
                      nudge it is useless after the fact (same fix as 1.2.0). */}
                  <span className="sp-up-save">Save 45% with yearly</span>
                </div>
              )}
              <div className="sp-up-planrow">
                <button
                  className={`sp-up-plan${upPlan === 'pro' ? ' on' : ''}`}
                  onClick={() => { setUpPlan('pro'); fx('rl_splitter_modal_toggle', { what: 'plan', value: 'pro' }); }}
                >
                  <span className="sp-up-badge">MOST POPULAR</span>
                  <span className="sp-up-planhead">
                    <span className="sp-up-radio" aria-hidden="true" />
                    <span className="sp-up-planname">Pro</span>
                  </span>
                  <span className="sp-up-price">
                    {upBilling === 'yearly' ? '$3.25' : '$5.95'} <i>/month</i>
                  </span>
                  <span className="sp-up-note">
                    {upBilling === 'yearly' ? 'Billed $39/year · cancel anytime' : 'Billed monthly · cancel anytime'}
                  </span>
                </button>
                <button
                  className={`sp-up-plan life${upPlan === 'lifetime' ? ' on' : ''}`}
                  onClick={() => { setUpPlan('lifetime'); fx('rl_splitter_modal_toggle', { what: 'plan', value: 'lifetime' }); }}
                >
                  <span className="sp-up-badge gold">BEST VALUE</span>
                  <span className="sp-up-planhead">
                    <span className="sp-up-radio" aria-hidden="true" />
                    <span className="sp-up-planname">Founder&rsquo;s Lifetime</span>
                  </span>
                  <span className="sp-up-price">$99 <i>one-time</i></span>
                  <span className="sp-up-note">Pay once, yours forever — launch offer</span>
                </button>
              </div>

            </div>
            </div>{/* /.sp-up-body */}

            <div className="sp-up-foot">
              <Link
                className={`sp-up-cta${upPlan === 'lifetime' ? ' gold' : ''}`}
                to={upPlan === 'lifetime'
                  ? '/checkout?plan=lifetime'
                  : `/checkout?plan=${upBilling === 'yearly' ? 'annual' : 'monthly'}`}
                onClick={() => {
                  markFunnel('splitter');
                  fx('rl_splitter_plan_click', {
                    plan: upPlan === 'lifetime' ? 'lifetime' : (upBilling === 'yearly' ? 'annual' : 'monthly'),
                    secs: Math.round((Date.now() - plansOpenedAt.current) / 1000),
                  });
                }}
              >
                {upPlan === 'lifetime'
                  ? 'Get Lifetime — $99 once'
                  : (upBilling === 'yearly' ? 'Get Pro — $39/year' : 'Get Pro — $5.95/month')}
              </Link>
              <div className="sp-up-footrow">
                <span>
                  {upPlan === 'lifetime'
                    ? 'All future Pro features included · locks in founder price'
                    : 'Unlocks the moment you pay · cancel anytime'}
                </span>
                {/* One exit, and it takes the trip. "Keep using free" — which
                    closed the dialog and left the free doorway on the row
                    behind — was removed on the owner's call, 2026-09-01. The
                    dialog is still dismissible by the ✕ and the backdrop, so
                    nobody is trapped; declining just no longer has its own
                    worded button. Not dressed as a button either way: it is an
                    alternative to the CTA, not a rival to it. */}
                <span className="sp-up-exits">
                  <button
                    className="sp-up-free go"
                    disabled={handingOff}
                    onClick={() => {
                      fx('rl_splitter_plan_click', { plan: 'free_studio', secs: Math.round((Date.now() - plansOpenedAt.current) / 1000) });
                      setPlansOpen(false);
                      setDeclinedFree(true);
                      void openInStudio();
                    }}
                  >
                    {handingOff ? 'Opening…' : 'Open in the Studio →'}
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
